/**
 * Stock Count — the mobile "simple scanning" screen (Suri, 2026-10-05).
 * Deliberately standalone, not reached from or tied to any ASN. PER
 * LOCATION (Suri, 2026-10-06: "scan needs to be done per location and
 * update the same location"): scan (or type) a LOCATION first — it stays
 * selected until changed — then scan (or type) each SKU in it and enter how
 * many are physically in THAT location, submit. The backend
 * (services/stockCount.ts) resolves the product against Mintsoft itself
 * and records the count; recounting the same SKU before it's applied
 * simply supersedes the earlier one ("latest scan wins"), so a mis-scan
 * or mis-count is fixed by just scanning again rather than needing an
 * undo/edit flow like the receiving scan screen has.
 *
 * No offline queue here yet (unlike scan.ts) — recording a count needs a
 * live Mintsoft lookup to resolve the SKU/current OnHand, so there's no
 * local item list to optimistically update against while offline. Not
 * built in this pass; flagged as a future enhancement if warehouse wifi
 * dead zones turn out to be a real problem for this screen too.
 */
import { BarcodeFormat, DecodeHintType } from '@zxing/library';
import { BrowserMultiFormatReader } from '@zxing/browser';
import { api, ApiError, ApiNetworkError } from '../api.js';
import { escapeHtml, formatTime, qs } from '../dom.js';
import { signal } from '../feedback.js';
import type { StockCount } from '../types.js';

const CAMERA_SCAN_HINTS = new Map<DecodeHintType, unknown>([
  [
    DecodeHintType.POSSIBLE_FORMATS,
    [BarcodeFormat.EAN_13, BarcodeFormat.EAN_8, BarcodeFormat.UPC_A, BarcodeFormat.UPC_E, BarcodeFormat.CODE_128],
  ],
]);

export async function renderStockCount(container: HTMLElement): Promise<void> {
  const div = document.createElement('div');
  div.className = 'screen';
  container.appendChild(div);

  div.innerHTML = `
    <div class="topbar">
      <h1>Stock Count</h1>
      <div class="actions-row"><button class="ghost" id="backBtn">Home</button><button class="ghost" id="refreshBtn">Refresh</button></div>
    </div>
    <div class="location-bar" id="locBar" style="display:flex; align-items:center; justify-content:space-between; gap:0.5rem; padding:0.6rem 0.75rem; margin:0.5rem 0; border:2px solid #888; border-radius:8px;"></div>
    <div id="sessionSummary"></div>
    <p class="hint" id="stepHint"></p>
    <div class="last-scan" id="lastScan"><div class="sku">Ready</div><div class="count">Scan a location to begin</div></div>
    <div class="scan-input-wrap">
      <input type="text" id="scanInput" placeholder="Scan or type" autocomplete="off" autocapitalize="off" autocorrect="off" spellcheck="false" />
      <button type="button" class="ghost camera-btn" id="cameraBtn" aria-label="Scan with camera">📷</button>
    </div>
    <button class="ghost" id="manualSkuBtn" style="margin-top:0.5rem;">No barcode — enter SKU manually</button>
    <h2 style="margin-top:1.5rem;">Pending counts (waiting for a supervisor to apply)</h2>
    <div class="list" id="pendingList"><p class="hint center">Loading…</p></div>
  `;

  const lastScanEl = qs<HTMLDivElement>(div, '#lastScan');
  const locBarEl = qs<HTMLDivElement>(div, '#locBar');
  const stepHintEl = qs<HTMLParagraphElement>(div, '#stepHint');
  const summaryEl = qs<HTMLDivElement>(div, '#sessionSummary');
  // Latest pending list, kept so the per-location summary can be recomputed without another request.
  let pendingCache: StockCount[] = [];
  // The location currently being counted — kept for the session so a whole
  // shelf can be counted without rescanning it per SKU. sessionStorage is
  // only a convenience (survives an accidental reload); it may throw or be
  // empty, so every access is guarded and the screen works without it.
  let currentLocation: string | null = null;
  try {
    currentLocation = sessionStorage.getItem('wr.stockCount.location');
  } catch {
    currentLocation = null;
  }
  const scanInput = qs<HTMLInputElement>(div, '#scanInput');
  const cameraBtn = qs<HTMLButtonElement>(div, '#cameraBtn');
  const pendingListEl = qs<HTMLDivElement>(div, '#pendingList');
  let stopCameraScan: (() => void) | null = null;
  let destroyed = false;

  qs<HTMLButtonElement>(div, '#backBtn').addEventListener('click', () => {
    location.hash = '#/';
  });
  qs<HTMLButtonElement>(div, '#refreshBtn').addEventListener('click', () => void loadPending());
  qs<HTMLButtonElement>(div, '#manualSkuBtn').addEventListener('click', () => {
    if (!currentLocation) {
      flash('bad', 'Location first', 'Scan or type the location before entering a SKU.');
      return;
    }
    openManualSkuModal();
  });

  function setLocation(value: string | null): void {
    currentLocation = value && value.trim() ? value.trim() : null;
    try {
      if (currentLocation) sessionStorage.setItem('wr.stockCount.location', currentLocation);
      else sessionStorage.removeItem('wr.stockCount.location');
    } catch {
      // storage unavailable — the in-memory value still works for this visit
    }
    renderLocationBar();
  }

  function renderLocationBar(): void {
    if (currentLocation) {
      locBarEl.style.borderColor = '#2e7d32';
      locBarEl.innerHTML = `<div><div class="hint" style="margin:0;">Counting in location</div><div style="font-size:1.8rem; font-weight:800;">${escapeHtml(currentLocation)}</div></div>
        <div style="display:flex; gap:0.5rem;"><button type="button" class="ghost" id="changeLocBtn">Change</button><button type="button" class="primary" id="finishLocBtn">Finish</button></div>`;
      qs<HTMLButtonElement>(locBarEl, '#changeLocBtn').addEventListener('click', () => {
        setLocation(null);
        flash('neutral', 'Ready', 'Scan the next location');
        focusInput();
      });
      qs<HTMLButtonElement>(locBarEl, '#finishLocBtn').addEventListener('click', () => openFinishLocationModal());
      stepHintEl.textContent = 'Scan or type a SKU, then enter how many are in this location. Tap a pending count to recount it, or ✕ to cancel a mistake.';
      scanInput.placeholder = 'Scan or type SKU / barcode';
    } else {
      locBarEl.style.borderColor = '#c62828';
      locBarEl.innerHTML = `<div><div style="font-size:1.2rem; font-weight:800;">Scan a LOCATION first</div><div class="hint" style="margin:0;">Scan the location label (or type it) below.</div></div>`;
      stepHintEl.textContent = 'Step 1: scan or type the location you are standing at. Step 2: scan each SKU in it and enter the quantity.';
      scanInput.placeholder = 'Scan or type location';
    }
    renderSummary();
  }

  /** Counts waiting to be applied in the location being counted right now. */
  function hereCounts(): StockCount[] {
    if (!currentLocation) return [];
    const wanted = currentLocation.trim().toLowerCase();
    return pendingCache.filter((c) => (c.locationName ?? '').toLowerCase() === wanted);
  }

  function summaryNumbers(rows: StockCount[]): { skus: number; units: number; differences: number } {
    return {
      skus: rows.length,
      units: rows.reduce((n, c) => n + c.countedQuantity, 0),
      differences: rows.filter((c) => c.mintsoftOnHandAtCount !== null && c.mintsoftOnHandAtCount !== c.countedQuantity).length,
    };
  }

  function renderSummary(): void {
    if (!currentLocation) {
      summaryEl.innerHTML = '';
      return;
    }
    const n = summaryNumbers(hereCounts());
    summaryEl.innerHTML = `<div class="sc-summary">
      <div><div class="n">${n.skus}</div><div class="l">SKUs counted</div></div>
      <div><div class="n">${n.units}</div><div class="l">units</div></div>
      <div class="${n.differences > 0 ? 'diff' : ''}"><div class="n">${n.differences}</div><div class="l">differ from Mintsoft</div></div>
    </div>`;
  }

  /** "Finish location": shows what was counted here, then clears the location so the next scan starts a new one. Nothing is sent — counts are already saved. */
  function openFinishLocationModal(): void {
    const rows = [...hereCounts()].sort((a, b) => a.sku.localeCompare(b.sku));
    const n = summaryNumbers(rows);
    const backdrop = document.createElement('div');
    backdrop.className = 'modal-backdrop';
    const list = rows
      .map((c) => {
        const d = c.mintsoftOnHandAtCount === null ? null : c.countedQuantity - c.mintsoftOnHandAtCount;
        const tag = d === null ? '' : d === 0 ? '<span class="tag-ok">matches</span>' : `<span class="tag-diff">${d > 0 ? '+' : ''}${d}</span>`;
        return `<div class="finish-row"><span>${escapeHtml(c.sku)}</span><span><strong>${c.countedQuantity}</strong> ${tag}</span></div>`;
      })
      .join('');
    backdrop.innerHTML = `
      <div class="modal">
        <h2>Finish ${escapeHtml(currentLocation ?? '')}?</h2>
        <p class="hint" style="margin:0 0 0.5rem 0;">${n.skus} SKU${n.skus === 1 ? '' : 's'} · ${n.units} units · ${n.differences} differ from Mintsoft. These are saved; a supervisor applies them from the admin page.</p>
        <div class="finish-list">${list || '<p class="hint">Nothing counted in this location yet.</p>'}</div>
        <div class="actions-row" style="margin-top:0.75rem;">
          <button class="ghost big" id="keepCounting">Keep counting</button>
          <button class="primary big" id="nextLocation">Next location</button>
        </div>
      </div>`;
    document.body.appendChild(backdrop);
    const close = () => {
      backdrop.remove();
      focusInput();
    };
    backdrop.addEventListener('click', (e) => {
      if (e.target === backdrop) close();
    });
    qs<HTMLButtonElement>(backdrop, '#keepCounting').addEventListener('click', close);
    qs<HTMLButtonElement>(backdrop, '#nextLocation').addEventListener('click', () => {
      const finished = currentLocation;
      setLocation(null);
      close();
      flash('good', `${finished} finished`, `${n.skus} SKUs · ${n.units} units — scan the next location`);
    });
  }

  function focusInput(): void {
    if (destroyed) return;
    scanInput.focus({ preventScroll: true });
  }

  function flash(kind: 'good' | 'warn' | 'bad' | 'neutral', title: string, subtitle: string): void {
    lastScanEl.classList.remove('flash-good', 'flash-bad', 'result-good', 'result-warn', 'result-bad');
    void lastScanEl.offsetWidth; // force reflow so repeated identical flashes still animate
    if (kind !== 'neutral') lastScanEl.classList.add(`result-${kind}`);
    lastScanEl.innerHTML = `<div class="sku">${escapeHtml(title)}</div><div class="count">${escapeHtml(subtitle)}</div>`;
    signal(kind);
  }

  async function loadPending(): Promise<void> {
    if (pendingCache.length === 0) pendingListEl.innerHTML = '<p class="hint center">Loading…</p>';
    try {
      const pending = await api.listStockCounts('PENDING');
      pendingCache = pending;
      renderSummary();
      renderPending(pending);
    } catch (err) {
      pendingListEl.innerHTML = `<p class="error-text">${escapeHtml(describeError(err))}</p>`;
    }
  }

  // Rebuilt with real DOM nodes (not a joined innerHTML string) so each
  // row can carry its own click (recount/"edit") and Cancel handlers —
  // same pattern scan.ts's renderItems uses. Added 2026-10-05 per Suri's
  // "no edit option etc" feedback: tapping a row re-opens the quantity
  // modal pre-addressed at that SKU (submitting it supersedes the row,
  // same "latest scan wins" behavior as a fresh scan — no separate edit
  // endpoint needed), and the ✕ button calls the new cancel endpoint to
  // withdraw a mistaken entry without any recount at all.
  function renderPending(pending: StockCount[]): void {
    pendingListEl.innerHTML = '';
    if (pending.length === 0) {
      pendingListEl.innerHTML = '<p class="hint">Nothing pending.</p>';
      return;
    }
    const here = currentLocation?.trim().toLowerCase();
    const isHere = (c: StockCount): boolean => !!here && (c.locationName ?? '').toLowerCase() === here;
    // This location's counts first (what the operator is working on), each group newest-first.
    const sorted = [...pending].sort((a, b) => Number(isHere(b)) - Number(isHere(a)) || b.countedAt.localeCompare(a.countedAt));
    for (const c of sorted) {
      const variance = c.mintsoftOnHandAtCount === null ? null : c.countedQuantity - c.mintsoftOnHandAtCount;
      const varianceText = variance === null ? '' : ` <span class="expected">(Mintsoft ${c.mintsoftOnHandAtCount}, ${variance > 0 ? '+' : ''}${variance})</span>`;
      const row = document.createElement('div');
      row.className = 'item-row';
      if (isHere(c)) row.classList.add('item-here');
      const where = c.locationName ? escapeHtml(c.locationName) : '<span class="error-text">no location (old count) — cancel it</span>';
      row.innerHTML = `
        <div class="pending-main" style="cursor:pointer;">
          <div class="sku">${escapeHtml(c.sku)}</div>
          <div class="ean">${where} · ${formatTime(c.countedAt)}${c.locationName ? ' · tap to recount' : ''}</div>
        </div>
        <div class="qty">${c.countedQuantity}${varianceText}</div>
        <button type="button" class="ghost cancel-pending" aria-label="Cancel this pending count" style="margin-left:0.5rem;">&times;</button>
      `;
      qs<HTMLDivElement>(row, '.pending-main').addEventListener('click', () => {
        if (c.locationName) openQuantityModal({ sku: c.sku }, c.locationName);
      });
      qs<HTMLButtonElement>(row, '.cancel-pending').addEventListener('click', (e) => {
        e.stopPropagation();
        void cancelPending(c.id, c.sku);
      });
      pendingListEl.appendChild(row);
    }
  }

  async function cancelPending(id: string, sku: string): Promise<void> {
    if (!confirm(`Cancel the pending count for ${sku}?`)) return;
    try {
      await api.cancelStockCount(id);
      await loadPending();
    } catch (err) {
      flash('bad', 'Error', describeError(err));
    }
  }

  async function submitCount(input: { ean?: string; sku?: string }, location: string, countedQuantity: number): Promise<void> {
    try {
      const outcome = await api.recordStockCount({ ...input, location, countedQuantity });
      if (outcome.kind === 'PRODUCT_NOT_FOUND') {
        flash('bad', 'Not found', input.ean ? `No product for barcode ${input.ean}` : `No product for SKU ${input.sku}`);
        return;
      }
      if (outcome.kind === 'LOCATION_NOT_FOUND') {
        flash('bad', 'Unknown location', `No location "${location}" — scan the location again.`);
        if (location === currentLocation) setLocation(null);
        return;
      }
      const { stockCount, supersededPreviousId } = outcome;
      const variance = stockCount.mintsoftOnHandAtCount === null ? null : stockCount.countedQuantity - stockCount.mintsoftOnHandAtCount;
      const where = `${stockCount.sku} @ ${stockCount.locationName ?? location}`;
      const replaced = supersededPreviousId ? ' · replaced earlier count' : '';
      if (variance === null) {
        flash('good', `Saved: ${where}`, `Counted ${stockCount.countedQuantity}${replaced}`);
      } else if (variance === 0) {
        flash('good', `✓ ${where}`, `Counted ${stockCount.countedQuantity} — matches Mintsoft${replaced}`);
      } else {
        flash('warn', `Saved: ${where}`, `Counted ${stockCount.countedQuantity} · Mintsoft ${stockCount.mintsoftOnHandAtCount} (${variance > 0 ? '+' : ''}${variance})${replaced}`);
      }
      void loadPending(); // refresh the list/summary in the background — the operator can already scan the next SKU
    } catch (err) {
      // The server answers 404 + LOCATION_NOT_FOUND (a thrown ApiError, not an outcome kind) for a bad location.
      if (err instanceof ApiError && err.code === 'LOCATION_NOT_FOUND') {
        flash('bad', 'Unknown location', `No location "${location}" — scan the location again.`);
        if (location === currentLocation) setLocation(null);
        return;
      }
      if (err instanceof ApiError && err.code === 'PRODUCT_NOT_FOUND') {
        flash('bad', 'Not found', input.ean ? `No product for barcode ${input.ean}` : `No product for SKU ${input.sku}`);
        return;
      }
      flash('bad', 'Error', describeError(err));
    }
  }

  function openQuantityModal(input: { ean?: string; sku?: string }, locationOverride?: string): void {
    const location = locationOverride ?? currentLocation;
    if (!location) {
      flash('bad', 'Location first', 'Scan or type the location before counting a SKU.');
      return;
    }
    const label = input.ean ? `Barcode ${input.ean}` : `SKU ${input.sku}`;
    const backdrop = document.createElement('div');
    backdrop.className = 'modal-backdrop';
    backdrop.innerHTML = `
      <div class="modal">
        <h2 id="qtyTitle">${escapeHtml(label)}</h2>
        <p class="hint" id="qtyProduct" style="margin:0;"></p>
        <p class="hint" style="margin:0 0 0.5rem 0;">Location: <strong>${escapeHtml(location)}</strong></p>
        <div class="expected-box" id="expectedBox">Checking Mintsoft…</div>
        <label for="countedQty">How many are in this location?</label>
        <div class="qty-row">
          <button type="button" class="ghost step" id="qtyMinus" aria-label="One less">&minus;</button>
          <input type="number" id="countedQty" min="0" step="1" inputmode="numeric" class="qty-input" autofocus />
          <button type="button" class="ghost step" id="qtyPlus" aria-label="One more">+</button>
        </div>
        <button class="match big" id="matchBtn" style="display:none;"></button>
        <p id="qtyStatus" class="hint"></p>
        <div class="actions-row">
          <button class="ghost big" id="cancelQty">Cancel</button>
          <button class="primary big" id="submitQty">Save count</button>
        </div>
      </div>
    `;
    document.body.appendChild(backdrop);
    let closed = false;
    const close = () => {
      closed = true;
      backdrop.remove();
      focusInput();
    };
    backdrop.addEventListener('click', (e) => {
      if (e.target === backdrop) close();
    });
    qs<HTMLButtonElement>(backdrop, '#cancelQty').addEventListener('click', close);

    const qtyInput = qs<HTMLInputElement>(backdrop, '#countedQty');
    const expectedBox = qs<HTMLDivElement>(backdrop, '#expectedBox');
    const matchBtn = qs<HTMLButtonElement>(backdrop, '#matchBtn');
    const submitBtn = qs<HTMLButtonElement>(backdrop, '#submitQty');
    const status = qs<HTMLParagraphElement>(backdrop, '#qtyStatus');
    qtyInput.focus();

    qs<HTMLButtonElement>(backdrop, '#qtyMinus').addEventListener('click', () => {
      qtyInput.value = String(Math.max(0, (Number(qtyInput.value) || 0) - 1));
    });
    qs<HTMLButtonElement>(backdrop, '#qtyPlus').addEventListener('click', () => {
      qtyInput.value = String((Number(qtyInput.value) || 0) + 1);
    });

    // Ask Mintsoft what it expects here. The keypad is usable immediately — this only fills in the hint,
    // and also catches a mis-scanned barcode before a quantity is typed.
    void api
      .expectedStockCount({ ...input, location })
      .then((info) => {
        if (closed) return;
        qs<HTMLHeadingElement>(backdrop, '#qtyTitle').textContent = info.sku;
        if (info.productName) qs<HTMLParagraphElement>(backdrop, '#qtyProduct').textContent = info.productName;
        if (info.expectedQuantity === null) {
          expectedBox.className = 'expected-box unknown';
          expectedBox.textContent = 'Mintsoft quantity unavailable — count what you see.';
        } else {
          const expected = info.expectedQuantity;
          expectedBox.className = 'expected-box';
          expectedBox.innerHTML = `Mintsoft expects <strong>${expected}</strong> in ${escapeHtml(info.locationName)}`;
          matchBtn.style.display = '';
          matchBtn.textContent = `✓ Matches — ${expected}`;
          matchBtn.addEventListener('click', () => void submit(expected));
        }
        if (info.pendingCountedQuantity !== null) {
          status.textContent = `You already counted ${info.pendingCountedQuantity} here (waiting to be applied) — saving replaces it.`;
        }
      })
      .catch((err) => {
        if (closed) return;
        if (err instanceof ApiError && err.code === 'PRODUCT_NOT_FOUND') {
          expectedBox.className = 'expected-box bad';
          expectedBox.textContent = input.ean ? `No product found for barcode ${input.ean}. Check the scan.` : `No product found for SKU ${input.sku}.`;
          submitBtn.disabled = true;
          return;
        }
        if (err instanceof ApiError && err.code === 'LOCATION_NOT_FOUND') {
          close();
          flash('bad', 'Unknown location', `No location "${location}" — scan the location again.`);
          if (location === currentLocation) setLocation(null);
          return;
        }
        expectedBox.className = 'expected-box unknown';
        expectedBox.textContent = `Could not check Mintsoft (${describeError(err)}) — you can still count.`;
      });

    async function submit(forced?: number): Promise<void> {
      const value = forced ?? (qtyInput.value.trim() === '' ? NaN : Number(qtyInput.value));
      if (!Number.isInteger(value) || value < 0) {
        status.classList.add('error-text');
        status.textContent = 'Enter a whole number, zero or greater.';
        return;
      }
      if (value > 100000) {
        status.classList.add('error-text');
        status.textContent = `${value} looks wrong — did a barcode get typed into the quantity box? Clear it and type the number you counted.`;
        qtyInput.select();
        return;
      }
      status.classList.remove('error-text');
      status.textContent = 'Saving…';
      close(); // straight back to scanning — the result shows in the big banner above
      flash('neutral', 'Saving…', `${input.sku ?? input.ean ?? ''} · ${value}`);
      await submitCount(input, location!, value);
    }

    submitBtn.addEventListener('click', () => void submit());
    qtyInput.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        void submit();
      }
    });
  }

  function openManualSkuModal(): void {
    const backdrop = document.createElement('div');
    backdrop.className = 'modal-backdrop';
    backdrop.innerHTML = `
      <div class="modal">
        <h2>Enter SKU</h2>
        <label for="manualSku">SKU</label>
        <input type="text" id="manualSku" autocomplete="off" autocapitalize="off" autocorrect="off" spellcheck="false" />
        <div class="actions-row">
          <button class="ghost big" id="cancelSku">Cancel</button>
          <button class="primary big" id="nextSku">Next</button>
        </div>
      </div>
    `;
    document.body.appendChild(backdrop);
    const close = () => {
      backdrop.remove();
      focusInput();
    };
    backdrop.addEventListener('click', (e) => {
      if (e.target === backdrop) close();
    });
    qs<HTMLButtonElement>(backdrop, '#cancelSku').addEventListener('click', close);
    const skuInput = qs<HTMLInputElement>(backdrop, '#manualSku');
    skuInput.focus();

    function next(): void {
      const sku = skuInput.value.trim();
      if (!sku) return;
      close();
      openQuantityModal({ sku });
    }
    qs<HTMLButtonElement>(backdrop, '#nextSku').addEventListener('click', next);
    skuInput.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        next();
      }
    });
  }

  scanInput.addEventListener('keydown', (e) => {
    if (e.key !== 'Enter') return;
    e.preventDefault();
    const value = scanInput.value.trim();
    scanInput.value = '';
    if (!value) return;
    if (!currentLocation) {
      setLocation(value);
      flash('neutral', value, 'Location set — now scan a SKU');
      return;
    }
    openQuantityModal({ ean: value });
  });
  scanInput.addEventListener('blur', () => {
    // Same re-focus-shortly-after-blur pattern as scan.ts, so the
    // hardware scanner's keyboard-emulated input keeps landing here
    // without the operator having to tap back in.
    setTimeout(() => {
      if (!document.querySelector('.modal-backdrop')) focusInput();
    }, 250);
  });
  renderLocationBar();
  focusInput();

  cameraBtn.addEventListener('click', () => openCameraScanModal());

  function openCameraScanModal(): void {
    if (!navigator.mediaDevices?.getUserMedia) {
      const backdrop = document.createElement('div');
      backdrop.className = 'modal-backdrop';
      backdrop.innerHTML = `
        <div class="modal">
          <h2>Camera scanning unavailable</h2>
          <p>This browser doesn't support camera access. Keep using the handheld scanner or type the barcode in.</p>
          <button class="ghost" id="closeBtn">Close</button>
        </div>
      `;
      document.body.appendChild(backdrop);
      const close = () => {
        backdrop.remove();
        focusInput();
      };
      backdrop.addEventListener('click', (e) => {
        if (e.target === backdrop) close();
      });
      qs<HTMLButtonElement>(backdrop, '#closeBtn').addEventListener('click', close);
      return;
    }

    const backdrop = document.createElement('div');
    backdrop.className = 'modal-backdrop';
    backdrop.innerHTML = `
      <div class="modal camera-modal">
        <h2>Scan with Camera</h2>
        <p class="hint" id="cameraStatus">Starting camera…</p>
        <video id="cameraVideo" autoplay playsinline muted></video>
        <button class="ghost" id="cancelCamera" style="margin-top:0.75rem;">Cancel</button>
      </div>
    `;
    document.body.appendChild(backdrop);

    const video = qs<HTMLVideoElement>(backdrop, '#cameraVideo');
    const statusEl = qs<HTMLParagraphElement>(backdrop, '#cameraStatus');
    let controls: { stop: () => void } | null = null;
    let stopped = false;

    const stop = () => {
      if (stopped) return;
      stopped = true;
      controls?.stop();
      stopCameraScan = null;
    };
    const close = () => {
      stop();
      backdrop.remove();
      focusInput();
    };
    stopCameraScan = close;

    backdrop.addEventListener('click', (e) => {
      if (e.target === backdrop) close();
    });
    qs<HTMLButtonElement>(backdrop, '#cancelCamera').addEventListener('click', close);

    const reader = new BrowserMultiFormatReader(CAMERA_SCAN_HINTS);
    let handled = false;

    void (async () => {
      try {
        controls = await reader.decodeFromConstraints(
          { video: { facingMode: 'environment' } },
          video,
          (result, err) => {
            if (stopped || handled) return;
            if (result) {
              handled = true;
              const value = result.getText();
              close();
              if (!currentLocation) {
                setLocation(value);
                flash('neutral', value, 'Location set — now scan a SKU');
              } else {
                openQuantityModal({ ean: value });
              }
              return;
            }
            void err; // fires every frame with no barcode found yet — not a real failure
          },
        );
        if (stopped) {
          controls.stop();
          return;
        }
        statusEl.textContent = 'Point the camera at a barcode.';
      } catch {
        statusEl.classList.add('error-text');
        statusEl.textContent = "Couldn't access the camera. Check permissions and try again.";
      }
    })();
  }

  void loadPending();

  const cleanup = () => {
    destroyed = true;
    stopCameraScan?.();
    window.removeEventListener('hashchange', cleanup);
  };
  window.addEventListener('hashchange', cleanup, { once: true });
}

function describeError(err: unknown): string {
  if (err instanceof ApiNetworkError) return 'Network error.';
  if (err instanceof ApiError) return `${err.message}${err.code ? ` (${err.code})` : ''}`;
  return String(err);
}
